import bind from 'bind-decorator';
import delay from 'delay';
import { debounce } from 'throttle-debounce';
import { fabric } from 'fabric';

import FabricNode from './fabric_node';
import { isMobile } from '@/helpers/mobile_detect';
import { BOUNDARY_OFFSET_X } from './view';

const FRONTEND_MAP_WIDTH = 348;

export default class SchemaImage extends FabricNode {
  initialize({ map, fabricData, schemaImageUrl }) {
    super.initialize(map, fabricData);

    this.isActivated = false;
    this.schemaUrl = schemaImageUrl || this.$node.data('schema_url');

    if (!this.schemaUrl) { return; }

    this._applyLanczos = debounce(25, this._applyLanczos);

    this._buildFabricNode(this._parseData());
  }

  activate() {
    if (!this.fabricNode) { return; }

    this.isActive = true;
    this.canvas
      .setActiveObject(this.fabricNode)
      .renderAll();
  }

  deactivate() {
    this.isActive = false;
  }

  get isActive() {
    return this.isActivated;
  }

  set isActive(value) {
    this.isActivated = value;

    if (this.fabricNode) {
      this.fabricNode.selectable = this.isSelectable;
      this.fabricNode.hoverCursor = this.hoverCursor;
    }
  }

  get isSelectable() {
    return this.isEditable && this.isActivated;
  }

  get hoverCursor() {
    return this.isSelectable ?
      'pointer' :
      'default';
  }

  // private methods
  _buildFabricNode(data) {
    fabric.Image.fromURL(this.schemaUrl, fabricNode => {
      this.fabricNode = fabricNode;
      this.isActive = this.isActivated;

      this.fabricNode
        .on('moving', this._syncInputs)
        .on('scaling', this._syncInputs)
        .on('resizing', this._syncInputs)
        .on('skewing', this._syncInputs)
        .on('rotating', this._syncInputs);

      // scaleToWidth logic https://github.com/fabricjs/fabric.js/blob/master/src/mixins/object_geometry.mixin.ts#L579
      this.canvasScaleRatio = (
        this.canvas.width -
          (BOUNDARY_OFFSET_X + this.map.xOffsetToCenter) * 2
      ) / (FRONTEND_MAP_WIDTH - BOUNDARY_OFFSET_X * 2);

      this._syncFabricNode(null, data);
      this.canvas.insertAt(this.fabricNode, -100);

      if (this.isEditable) {
        this._bindInputs();
        this._syncInputs();
      }

      delay(50).then(this._initLanczos);
    }, {
      ...this.defaultFabricOptions,
      hoverCursor: this.hoverCursor,
      crossOrigin: 'anonymous'
    });
  }

  @bind
  _applyLanczos() {
    // телефоны падают при запуске этой штуки
    if (isMobile()) { return; }

    const currentZoom = this.canvas.getZoom();
    const r = this.canvas.getRetinaScaling();
    const sX = Math.abs(this.fabricNode.scaleX) * r * currentZoom;
    const sY = Math.abs(this.fabricNode.scaleY) * r * currentZoom;
    const lanczosZoomThreshold = this.map.maxZoom / 2;

    let scaleX = 1;
    let scaleY = 1;
    let lanczosLobes = null;

    if (currentZoom < lanczosZoomThreshold) {
      if (sX > 0.01 && sY > 0.01 && sX < 1 && sY < 1) {
        if (sX <= 0.2 || sY <= 0.2) {
          lanczosLobes = 2;
        } else if (sX <= 0.05 || sY <= 0.05) {
          lanczosLobes = 1;
        } else {
          lanczosLobes = 3;
        }
        scaleX = sX;
        scaleY = sY;
      }
    }

    const isChanged = this._isLanczosChanged(scaleX, scaleY) ||
      currentZoom !== this.prevZoom;
    const isLanczos = scaleX !== 1 || scaleY !== 1;
    this.prevZoom = currentZoom;

    if (!isChanged) { return; }

    if (isLanczos) {
      this.fabricNode.filters = [
        new fabric.Image.filters.Resize({
          resizeType: 'lanczos',
          lanczosLobes,
          scaleX,
          scaleY
        })
      ];
    } else {
      this.fabricNode.filters = [];
    }

    this.fabricNode.applyFilters();
    this.canvas.requestRenderAll();
  }

  @bind
  _initLanczos() {
    this._applyLanczos();

    this.fabricNode.on('scaling', this._applyLanczos);
    this.canvas.on('canvas:zoom', this._trackZoom);
  }

  @bind
  _trackZoom() {
    this._applyLanczos();
  }

  _isLanczosChanged(scaleX, scaleY) {
    const filter = this.fabricNode.filters[0];

    return !!(
      (!filter && (scaleX !== 1 || scaleY !== 1)) ||
        (filter && (filter.scaleX !== scaleX || filter.scaleY !== scaleY))
    );
  }
}
